import React, { useState, useEffect, useRef } from 'react';
import { SHOW_CC_HINTS } from '../../featureFlags';

/**
 * FloatingTopIcons — fixed-position duplicate of the Control Centre + Menu
 * icons. Slides in from the top edge when:
 *   • The original top icons are scrolled out of view, AND
 *   • The user has stopped scrolling for more than 3 seconds.
 *
 * Any new scroll event hides the floating icons immediately and restarts
 * the idle timer.
 *
 * The "Heads up" CC callout rides along with these icons (anchored beside
 * the floating CC icon) so it only appears AFTER the user has dropped to
 * the result and stayed put for >3s — keeping the result-reading
 * experience clean and revealing the prompt at a natural rest point.
 *
 * Props:
 *   originalAnchorRef         — ref to the inline icons row
 *   onOpenCC, onOpenMenu      — click handlers (same as inline icons)
 *   isMobile
 *   showCCCallout             — whether the "Heads up" callout is allowed
 *                               (parent gates: !hiddenTooltips['cc_first_query']
 *                               && queryThread.length >= 1)
 *   onCCCalloutDismiss        — Don't Show handler
 */
export const FloatingTopIcons = ({
  originalAnchorRef,
  onOpenCC,
  onOpenMenu,
  isMobile,
  showCCCallout = false,
  onCCCalloutDismiss = () => {},
}) => {
  const [visible, setVisible] = useState(false);
  const idleTimerRef = useRef(null);
  const IDLE_MS = 3000;

  useEffect(() => {
    const anchorStillVisible = () => {
      const node = originalAnchorRef?.current;
      if (!node) return false;
      const rect = node.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    };

    const scheduleShow = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (!anchorStillVisible()) setVisible(true);
      }, IDLE_MS);
    };

    const handleScroll = () => {
      setVisible(false);
      scheduleShow();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    scheduleShow();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [originalAnchorRef]);

  if (isMobile) return null;

  const size = 44;
  const slideClass = visible
    ? 'opacity-100 translate-y-0 pointer-events-auto'
    : 'opacity-0 -translate-y-8 pointer-events-none';

  return (
    <>
      {/* CC icon — top-left */}
      <div
        className={`fixed z-[9998] group transition-all duration-500 ease-out ${slideClass}`}
        style={{ top: '18px', left: '18px' }}
        data-testid="floating-control-centre-wrap"
      >
        <button
          onClick={onOpenCC}
          className="transition-transform hover:scale-105 rounded-full"
          style={{
            padding: '6px',
            backgroundColor: 'rgba(8,30,42,0.72)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(34,211,238,0.45)',
            boxShadow:
              '0 6px 20px rgba(0,0,0,0.45), 0 0 18px rgba(34,211,238,0.18)',
          }}
          data-testid="floating-control-centre-btn"
          aria-label="Open Control Centre"
        >
          <img
            src="/icons/control-centre.svg"
            alt="Control Centre"
            style={{ height: size, width: size, display: 'block' }}
          />
        </button>
        {/* Hover tag — same style as the inline CC icon tag. Hidden under
            SHOW_CC_HINTS for board-demo posture. */}
        {SHOW_CC_HINTS && (
          <span
            className="absolute -bottom-12 left-0 px-2 py-1 bg-gray-800 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100] text-center leading-tight"
            style={{ width: '70px' }}
          >
            Control<br />Centre
          </span>
        )}
      </div>

      {/* Heads Up CC callout — rides with the floating icons. Renders only
          when the floating icons are visible AND the parent allows it.
          Anchored beside the CC icon (top-left of viewport). Whole block also
          hidden under SHOW_CC_HINTS for board-demo posture. */}
      {SHOW_CC_HINTS && (
      <div
        className={`fixed z-[9998] transition-all duration-500 ease-out ${
          visible && showCCCallout
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-8 pointer-events-none'
        }`}
        style={{ top: '18px', left: `${18 + 44 + 12 + 16}px`, width: '360px' }}
        data-testid="floating-cc-callout"
      >
        <div
          className="rounded-xl shadow-2xl relative"
          style={{
            background:
              'linear-gradient(135deg, rgba(8,30,42,0.97) 0%, rgba(15,40,60,0.97) 100%)',
            border: '1px solid rgba(34,211,238,0.55)',
            boxShadow:
              '0 0 0 1px rgba(34,211,238,0.35), 0 12px 32px rgba(0,0,0,0.6), 0 0 28px rgba(34,211,238,0.25)',
          }}
        >
          {/* Pointer arrow — points back at the floating CC icon */}
          <div
            className="absolute w-3 h-3 rotate-45"
            style={{
              left: '-7px',
              top: '22px',
              background: 'rgba(8,30,42,0.97)',
              borderLeft: '1px solid rgba(34,211,238,0.55)',
              borderBottom: '1px solid rgba(34,211,238,0.55)',
            }}
          />
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-400 text-black text-xs font-bold">
                !
              </span>
              <span className="text-xs uppercase tracking-[0.18em] text-cyan-300 font-semibold">
                Heads up
              </span>
            </div>
            <p className="text-sm text-gray-100 leading-relaxed">
              Please check out your{' '}
              <span className="font-bold text-cyan-300">Control Centre</span>{' '}
              and your{' '}
              <span className="font-bold text-cyan-300">Accuracy Controls</span>.
              We wanted to give you full control over your data and make it as
              simple as possible at the same time.
            </p>
            <div className="mt-3 pt-3 border-t border-cyan-400/20 flex items-center justify-between gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenCC();
                }}
                className="text-xs font-semibold bg-cyan-500 hover:bg-cyan-400 text-black px-3 py-1.5 rounded-md transition-colors"
                data-testid="floating-cc-callout-open"
              >
                Open Control Centre
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCCCalloutDismiss();
                }}
                className="text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-200 transition-colors"
                data-testid="floating-cc-callout-dismiss"
              >
                Don't Show
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Menu icon — top-right */}
      <div
        className={`fixed z-[9998] group transition-all duration-500 ease-out ${slideClass}`}
        style={{ top: '18px', right: '18px' }}
        data-testid="floating-menu-wrap"
      >
        <button
          onClick={onOpenMenu}
          className="transition-transform hover:scale-105 rounded-full"
          style={{
            padding: '6px',
            backgroundColor: 'rgba(8,30,42,0.72)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(34,211,238,0.45)',
            boxShadow:
              '0 6px 20px rgba(0,0,0,0.45), 0 0 18px rgba(34,211,238,0.18)',
          }}
          data-testid="floating-menu-btn"
          aria-label="Open Menu"
        >
          <img
            src="/icons/menu.svg"
            alt="Menu"
            style={{ height: size, width: size, display: 'block' }}
          />
        </button>
        <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-xs text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
          Menu
        </span>
      </div>
    </>
  );
};

export default FloatingTopIcons;
